import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import styles from "./DefaultSidebar.module.scss";

const NestedSideBarChildren = ({ data, index, opened, setOpened }) => {
  return (
    <ul className="nav-dropdown-items">
      {data.map((item, key) => {
        return (
          <li
            key={key}
            className={`nav-item ${
              !!item.children && item.children.length > 0
                ? `nav-dropdown ${
                    opened.findIndex((i) => i === `${index}.${key}`) >= 0
                      ? "open"
                      : ""
                  }`
                : ""
            }`}
          >
            {!!item.children && item.children.length > 0 ? (
              <>
                <a
                  className="nav-link nav-dropdown-toggle"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();

                    if (opened.findIndex((i) => i === `${index}.${key}`) >= 0) {
                      setOpened([
                        ...opened.filter((j) => j !== `${index}.${key}`),
                      ]);
                    } else {
                      setOpened([...opened, `${index}.${key}`]);
                    }
                  }}
                >
                  {item.name}
                  {!!item.badge ? (
                    <span className={`badge badge-${item.badge.variant}`}>
                      {item.badge.text}
                    </span>
                  ) : null}
                </a>
                <NestedSideBarChildren
                  data={item.children}
                  index={`${index}.${key}`}
                  opened={opened}
                  setOpened={setOpened}
                />
              </>
            ) : (
              <NavLink
                className={(active) => `nav-link ${!!active ? "active" : ""}`}
                to={item.url}
              >
                <i className={`nav-icon ${item.icon}`}></i>
                {item.name}
                {!!item.badge ? (
                  <span className={`badge badge-${item.badge.variant}`}>
                    {item.badge.text}
                  </span>
                ) : null}
              </NavLink>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const DefaultSidebar = ({ navConfig }) => {
  const [opened, setOpened] = useState([]);
  const [hoverName, setHoverName] = useState("");

  
    const containerRef = useRef(null);
  const contentRef = useRef(null);

  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const recalculate = () => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const visibleHeight = container.offsetHeight;
    const contentHeight = content.scrollHeight;

    const scrollable = contentHeight > visibleHeight;
    setIsScrollable(scrollable);

    if (scrollable) {
      const newThumbHeight = Math.max((visibleHeight / contentHeight) * visibleHeight, 30);
      setThumbHeight(newThumbHeight);
      const maxScrollTop = contentHeight - visibleHeight;
      setScrollTop((prev) => Math.min(prev, maxScrollTop));
    } else {
      setThumbHeight(0);
      setScrollTop(0);
    }
  };

  useEffect(recalculate, [opened]);

  useEffect(() => {
    const handleResize = () => recalculate();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const handleWheel = (e) => {
      e.preventDefault();
      const visibleHeight = container.offsetHeight;
      const contentHeight = content.scrollHeight;
      const maxScrollTop = contentHeight - visibleHeight;

      setScrollTop((prev) => {
        const next = Math.max(0, Math.min(prev + e.deltaY, maxScrollTop));
        return next;
      });
    };

    container.addEventListener('wheel', handleWheel);
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // ✨ Touch scrolling support
  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    let startY = 0;
    let startScrollTop = 0;

    const onTouchStart = (e) => {
      if (e.touches.length === 1) {
        startY = e.touches[0].clientY;
        startScrollTop = scrollTop;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches.length === 1) {
        const deltaY = e.touches[0].clientY - startY;
        const visibleHeight = container.offsetHeight;
        const contentHeight = content.scrollHeight;
        const maxScrollTop = contentHeight - visibleHeight;

        const newScrollTop = Math.max(0, Math.min(startScrollTop - deltaY, maxScrollTop));
        setScrollTop(newScrollTop);
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
    };
  }, [scrollTop]);

  const handleThumbDrag = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startScrollTop = scrollTop;

    const onMouseMove = (moveEvent) => {
      const container = containerRef.current;
      const content = contentRef.current;
      if (!container || !content) return;

      const visibleHeight = container.offsetHeight;
      const contentHeight = content.scrollHeight;
      const deltaY = moveEvent.clientY - startY;
      const scrollDelta = (deltaY / visibleHeight) * contentHeight;

      const maxScrollTop = contentHeight - visibleHeight;
      const newScrollTop = Math.max(0, Math.min(startScrollTop + scrollDelta, maxScrollTop));
      setScrollTop(newScrollTop);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  return (
    <>
      <div className={`scrollbar-container sidebar-nav ${styles['custom-sidebar']}`} ref={containerRef}>
        <ul
          className="nav"
          ref={contentRef}
          style={{
            transform: `translateY(-${scrollTop}px)`,
            transition: "transform 0.05s linear",
          }}
        >
          {(navConfig.items ?? []).map((item, key) => {
            return (
              <li
                key={key}
                className={`nav-item ${
                  !!item.children && item.children.length > 0
                    ? `nav-dropdown ${
                        opened.findIndex((i) => i === key) >= 0 ? "open" : ""
                      }`
                    : ""
                }`}
              >
                {!!item.children && item.children.length > 0 ? (
                  <>
                    <a
                      className="nav-link nav-dropdown-toggle"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();

                        if (opened.findIndex((i) => i === key) >= 0) {
                          setOpened([...opened.filter((j) => j !== key)]);
                        } else {
                          setOpened([...opened, key]);
                        }
                      }}
                      onMouseEnter={() => {
                        setHoverName(key);
                      }}
                      onMouseLeave={() => {
                        setHoverName("");
                      }}
                    >
                      {typeof item.icon === "string" ? (
                        <i className={`nav-icon ${item.icon}`}></i>
                      ) : (
                        <>
                          {typeof item.icon === "function"
                            ? item.icon({
                                className: "nav-icon",
                                hover: hoverName === key,
                              })
                            : null}
                        </>
                      )}
                      {item.name}
                      {!!item.badge ? (
                        <span className={`badge badge-${item.badge.variant}`}>
                          {item.badge.text}
                        </span>
                      ) : null}
                    </a>
                    <NestedSideBarChildren data={item.children} index={key} opened={opened} setOpened={setOpened}/>
                  </>
                ) : (
                  <NavLink
                    className={(active) =>
                      `nav-link ${!!active ? "active" : ""}`
                    }
                    to={item.url}
                  >
                    <i className={`nav-icon ${item.icon}`}></i>
                    {item.name}
                    {!!item.badge ? (
                      <span className={`badge badge-${item.badge.variant}`}>
                        {item.badge.text}
                      </span>
                    ) : null}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
        {isScrollable && (
        <div
          style={{
            width: '4px',
            height: '100%',
            background: '#eee',
            position: 'absolute',
            top: 0,
            right:0
          }}
          className={`${styles['scroll']}`}
        >
          
            <div
              onMouseDown={handleThumbDrag}
              style={{
                position: 'absolute',
                top: `${
                  (scrollTop / (contentRef.current?.scrollHeight || 1)) *
                  containerRef.current?.offsetHeight
                }px`,
                height: `${thumbHeight}px`,
                width: '100%',
                background: '#888',
                cursor: 'grab',
                borderRadius: '10px',
              }}
            />
        </div>
        
          )}
      </div>
    </>
  );
};

export default DefaultSidebar;
